/**
 * 课程报名时间窗口工具（单一来源）
 *
 * 场景：`getEnrollmentById` 返回的 start_date / end_date 决定「立即报名」按钮的显隐与可用性。
 * 字段缺失/解析失败 → fail-open（按钮正常显示，不锁死全员）。
 * H5 端加 0.1s 安全余量，start 早 0.1s 算开始、end 晚 0.1s 算截止，宽容本地时钟微偏差。
 *
 * ponytail: 不做秒级倒计时，调用方用 100ms setInterval + 状态对比节流重算；
 *          visibilitychange 触发立即重算（防后台冻结）。最大误差 < 100ms。
 *          后端必须同时做时间校验作为兜底（前端可被改手机时间绕过）。
 */

const TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/;

// H5 端 0.1s 安全余量（往「宽容」偏，UX 上到点就能用）
const SAFETY_MS = 100;

/**
 * 解析后端时间字段
 * @param {string|number|Date|null|undefined} value
 * @returns {Date|null} 解析失败返回 null
 */
export function parseDateTime(value) {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value !== "string") return null;
  // 手拆 YYYY-MM-DD HH:mm:ss（iOS Safari 对 "-" 分隔的 datetime 字符串返回 Invalid Date）
  const m = value.match(TIME_PATTERN);
  if (m) {
    return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
  }
  // 兜底：兼容 ISO8601 等其它格式（如带 T、Z、时区）
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * 格式化为完整日期时间：「YYYY-MM-DD HH:mm:ss」
 * ponytail: 一律完整显示，不做任何智能省略；
 *          用户 2026-09-10 要求"完整显示年月日时分秒"；与后端字段格式完全一致，零认知成本。
 * @param {Date} date
 * @returns {string}
 */
export function formatDateTime(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return "";
  const Y = date.getFullYear();
  const M = String(date.getMonth() + 1).padStart(2, "0");
  const D = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  const ss = String(date.getSeconds()).padStart(2, "0");
  return `${Y}-${M}-${D} ${hh}:${mm}:${ss}`;
}

/**
 * 计算报名按钮状态
 * @param {object} detail - getEnrollmentById 返回的 result 对象
 * @param {Date} [now] - 当前时间，默认 new Date()；测试可注入
 * @returns {{text:string, type:string, disabled:boolean, action:('showPopup'|'showToast'|null), reason:string}}
 *
 * action:
 *   - 'showPopup' 点击打开报名弹窗
 *   - 'showToast' 点击按 reason 弹 Toast 提示
 *   - null         不可点（已报名态）
 */
export function getEnrollWindowState(detail, now = new Date()) {
  // ① 已报名：不受窗口影响，永远显示
  if (detail && detail.registration_status == 1) {
    return {
      text: "已报名",
      type: "success",
      disabled: true,
      action: null,
      reason: "",
    };
  }

  const start = parseDateTime(detail && detail.start_date);
  const end = parseDateTime(detail && detail.end_date);

  // ② fail-open：任一字段缺失/解析失败 → 视为无门禁
  if (!start || !end) {
    return {
      text: "立即报名",
      type: "danger",
      disabled: false,
      action: "showPopup",
      reason: "",
    };
  }

  // ③ 窗口判断（带 0.1s 余量：start 早放行、end 晚截止）
  const startThreshold = start.getTime() - SAFETY_MS;
  const endThreshold = end.getTime() + SAFETY_MS;
  const nowMs = now.getTime();

  if (nowMs >= startThreshold && nowMs <= endThreshold) {
    return {
      text: "立即报名",
      type: "danger",
      disabled: false,
      action: "showPopup",
      reason: "",
    };
  }

  if (nowMs < startThreshold) {
    return {
      text: `报名未开始（${formatDateTime(start)} 开始）`,
      type: "success",
      customClass: "btn-enroll-disabled",
      disabled: false,
      action: "showToast",
      reason: "未到报名时间",
    };
  }

  // nowMs > endThreshold
  return {
    text: "报名已截止",
    type: "success",
    customClass: "btn-enroll-disabled",
    disabled: false,
    action: "showToast",
    reason: "报名已结束",
  };
}